<template>
    <div class="flex h-100vh">
        <div class="relative w-15 bg-#304156">
            <div class="h-15 w-15 flex items-center justify-center bg-#000011">
                <TpSvgIcon name="suanzhou-sail" :size="40" color="#FFFFFF" />
            </div>
            <div class="divCursor">
                <template v-for="(item, index) in menuList" :key="item.groupVO?.id || index">
                    <div
                        class="flex justify-center flex-col items-center h-12.5 mx-1.25 my-5"
                        :class="index === activeMenuGroupIndex ? ['bg-#ED752F'] : []"
                        @click="groupClickHandle(item, index)"
                    >
                        <div>
                            <el-icon :size="18" color="#fff">
                                <component :is="ICON_MAP[getGroupIconName(item)] || House" />
                            </el-icon>
                        </div>
                        <div class="text-3 text-white font-bold">{{ item.groupVO?.name || '' }}</div>
                    </div>
                </template>
            </div>
        </div>
        <div class="w-52 bg-white dark:bg-[var(--el-bg-color-overlay)]">
            <div class="menu-group-title">
                {{ menuList[activeMenuGroupIndex]?.groupVO?.name || '' }}
            </div>
            <el-menu
                :default-active="activeName"
                overflow-y-scroll
                class="w-52 h-[calc(100vh-60px)]"
                :collapse="isCollapse"
                :unique-opened="false"
            >
                <template v-for="menu in menuList[activeMenuGroupIndex]?.menuList || []" :key="menu.menuId">
                    <el-menu-item
                        v-if="(!menu.children || !menu.children.length) && menu.meta && !menu.meta.hiddenFlag"
                        :index="menu.componentName"
                        @click="menuClickHandle(menu)"
                    >
                        <template #title>{{ menu.meta ? menu.meta.title : '' }}</template>
                    </el-menu-item>
                    <el-sub-menu
                        v-else-if="menu.children && menu.children.length && menu.meta && !menu.meta.hiddenFlag"
                        :index="String(menu.menuId)"
                    >
                        <template #title>
                            <span>{{ menu.meta ? menu.meta.title : '' }}</span>
                        </template>
                        <template v-for="secondMenu in menu.children" :key="secondMenu.menuId">
                            <!-- 三级菜单（有子节点）-->
                            <el-sub-menu
                                v-if="
                                    secondMenu.children &&
                                    secondMenu.children.length &&
                                    secondMenu.meta &&
                                    !secondMenu.meta.hiddenFlag
                                "
                                :index="String(secondMenu.menuId)"
                            >
                                <template #title>
                                    <span>{{ secondMenu.meta ? secondMenu.meta.title : '' }}</span>
                                </template>
                                <template v-for="thirdMenu in secondMenu.children" :key="thirdMenu.menuId">
                                    <el-menu-item
                                        v-if="thirdMenu.meta ? !thirdMenu.meta.hiddenFlag : false"
                                        :index="thirdMenu.componentName"
                                        @click="menuClickHandle(thirdMenu)"
                                    >
                                        {{ thirdMenu.meta ? thirdMenu.meta.title : '' }}
                                    </el-menu-item>
                                </template>
                            </el-sub-menu>
                            <!-- 二级菜单（无子节点）-->
                            <el-menu-item
                                v-else-if="secondMenu.meta ? !secondMenu.meta.hiddenFlag : false"
                                :index="secondMenu.componentName"
                                @click="menuClickHandle(secondMenu)"
                            >
                                {{ secondMenu.meta ? secondMenu.meta.title : '' }}
                            </el-menu-item>
                        </template>
                    </el-sub-menu>
                </template>
            </el-menu>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useUserStore } from '@/stores/user';
import { House, User, OfficeBuilding, Box, Tickets, Grid, Setting } from '@element-plus/icons-vue';
import { storeToRefs } from 'pinia';

import router from '@/router';

import { useCollapseStore } from '@/stores/collapse';

// 获取本地菜单分类数据
const userStore = useUserStore();
const { menuList } = storeToRefs(userStore);

const activeName = computed(() => {
    return router.currentRoute.value.name;
});

function findPathIndexInGroup(menus: any[] | undefined, targetPath: string): number {
    if (!menus || !menus.length) {
        return -1;
    }
    for (let i = 0; i < menus.length; i++) {
        const menu = menus[i];
        if (menu.path === targetPath) {
            return i;
        }
        if (menu.children && menu.children.length > 0) {
            const childIndex = findPathIndexInGroup(menu.children, targetPath);
            if (childIndex !== -1) {
                return i; // 返回父菜单的索引
            }
        }
    }
    return -1; // 没有找到
}

function findPathIndex(data: any[] | undefined, targetPath: string): number {
    if (!data || !data.length) {
        return -1;
    }
    for (let i = 0; i < data.length; i++) {
        const group = data[i];
        const indexInGroup = findPathIndexInGroup(group?.menuList, targetPath);
        if (indexInGroup !== -1) {
            return i; // 返回父菜单的索引
        }
    }
    return -1; // 没有找到
}

const activeMenuGroupIndex = ref(0);

function updateActiveMenuGroupIndex() {
    const index = findPathIndex(menuList.value, router.currentRoute.value.path);
    // 只在命中分组时切换：未命中（-1，如 404 / 无对应菜单的路由）保持当前分组，
    // 避免 `menuList[-1]` 让右侧面板标题与菜单整块变空
    if (index !== -1) {
        activeMenuGroupIndex.value = index;
    }
}

watch(() => router.currentRoute.value.path, updateActiveMenuGroupIndex, { immediate: true });

//
function isActivePath(path: string) {
    return router.currentRoute.value.path === path;
}

const collapseStore = useCollapseStore();
const { isCollapse } = storeToRefs(collapseStore);

const emit = defineEmits<{
    (e: 'menu-click'): void;
}>();

//
const menuClickHandle = function (menu: any) {
    if (!menu.path) return;
    router.push({ path: menu.path });
    emit('menu-click');
};

//
function findFirstValidLeafPath(menus: any[]): string | null {
    if (!menus || !menus.length) return null;
    for (const menu of menus) {
        const isLeaf = !menu.children || !menu.children.length;
        if (isLeaf && menu.path) {
            return menu.path;
        }
        if (menu.children && menu.children.length) {
            const childPath = findFirstValidLeafPath(menu.children);
            if (childPath) return childPath;
        }
    }
    return null;
}

const groupClickHandle = function (item: any, index: number) {
    activeMenuGroupIndex.value = index;
    const targetPath = findFirstValidLeafPath(item.menuList);
    if (targetPath) {
        router.push({ path: targetPath });
    }
    emit('menu-click');
};

/** 分组默认图标映射（当后台未返回 icon 时兜底） */
const DEFAULT_GROUP_ICONS: Record<string, string> = {
    首页: 'house',
    顾客: 'user',
    商家: 'office-building',
    产品: 'box',
    订单: 'tickets',
    应用: 'grid',
    设置: 'setting'
};

/** 图标组件映射表 */
const ICON_MAP: Record<string, any> = {
    house: House,
    user: User,
    'office-building': OfficeBuilding,
    box: Box,
    tickets: Tickets,
    grid: Grid,
    setting: Setting
};

/** 获取分组对应的图标名称（小写连字符格式） */
function getGroupIconName(item: any): string {
    const icon = item.groupVO?.icon;
    if (icon) {
        // 兼容各种格式：House, house, ep:house, i-ep:house
        return icon
            .toLowerCase()
            .replace(/^i-ep:/, '')
            .replace(/^ep:/, '');
    }
    const name = item.groupVO?.name;
    return name ? DEFAULT_GROUP_ICONS[name] || 'house' : 'house';
}
</script>
<style lang="scss">
::-webkit-scrollbar {
    display: none;
    -ms-overflow-style: none;
    width: 0 !important;
}

.divCursor {
    cursor: pointer;
}

/* 分组标题 — 与左侧LOGO底部(60px)对齐 */
.menu-group-title {
    height: 60px;
    line-height: 60px;
    padding: 0 20px;
    font-size: 16px;
    font-weight: 700;
    color: #1f1f1f;
    border-bottom: 1px solid #f0f0f0;
    text-align: center;
}

/* 菜单整体 */
.el-menu {
    border-right: none !important;
}

/* 一级菜单项（无子菜单） */
.el-menu > .el-menu-item {
    height: 40px;
    line-height: 40px;
    margin: 0;
    margin-left: 2px;
    padding: 0 20px !important;
    font-size: 14px;
    color: #333;
    transition: all 0.15s ease;
}

.el-menu > .el-menu-item:hover {
    background-color: #f5f5f5;
}

.el-menu > .el-menu-item.is-active {
    background-color: #fff2e8;
    color: var(--el-color-primary);
    font-weight: 600;
}

/* 子菜单标题 */
.el-sub-menu__title {
    height: 40px;
    line-height: 40px;
    margin: 0;
    padding: 0 20px !important;
    font-size: 14px;
    font-weight: 500;
    color: #333;
    transition: all 0.15s ease;
}

.el-sub-menu__title:hover {
    background-color: #f5f5f5 !important;
}

/* 子菜单展开区域 */
.el-sub-menu .el-menu {
    padding: 0;
    background-color: #fff;
    border: none;
}
.el-sub-menu {
    padding-left: 2px;
}

/* 子菜单项 — 相对一级缩进，形成层次 */
.el-sub-menu .el-menu-item {
    height: 36px;
    line-height: 36px;
    margin: 0;
    padding: 0 20px 0 40px !important;
    font-size: 14px;
    color: #595959;
    transition: all 0.15s ease;
    position: relative;
    border: none !important;
    box-sizing: border-box;
}

.el-sub-menu .el-menu-item:hover {
    background-color: #f5f5f5;
    color: #262626;
}

.el-sub-menu .el-menu-item.is-active {
    background-color: #fff2e8;
    color: var(--el-color-primary);
    font-weight: 600;
}

/* 激活态左侧竖线（所有菜单项通用） */
.el-menu-item.is-active::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 16px;
    background-color: var(--el-color-primary);
    //border-radius: 0 2px 2px 0;
}

/* 二级子菜单标题（有三级菜单时） */
.el-sub-menu .el-sub-menu .el-sub-menu__title {
    padding-left: 40px !important;
    font-size: 14px;
    color: #595959;
}

/* 三级子菜单展开区域 */
.el-sub-menu .el-sub-menu .el-menu {
    padding: 0;
    background-color: #fff;
}

/* 三级菜单项 — 再多缩进一层 */
.el-sub-menu .el-sub-menu .el-menu-item {
    padding-left: 60px !important;
    font-size: 14px;
    color: #595959;
    height: 36px;
    line-height: 36px;
}

/* 箭头位置微调 */
.el-sub-menu__icon-arrow {
    right: 16px;
}
</style>
